import React from 'react';
import AppIconWithBackground from '@components/AppIconWithBackground';
import { External } from '@fabric-ui/icons';
import { Assets, Findings } from '@fabric-ui/icons/app-icons';
import { PolicyViolations } from '@fabric-ui/icons/screens';
import { iconSize, isDeepEqual } from '@fabric-ui/utils';
import { Box, capitalize } from '@mui/material';
import { APP_PATHS, PAGE_PATHS } from '../../types';
import {
  BaseScore,
  ConfigType,
  EditFactorType,
  EntitiesFactor,
  EntityBuildingBlock,
  EntityWeight,
  GetRiskScoringConfigResponse,
  KeyFactor,
  Factor as RiskFactor,
  ScoringRule,
} from './types';

export type ScoreType = string;

export const CONFIG_TYPE_DEFAULT = 'DEFAULT';

export const isFlatFactorsLayout = (configTypes: ConfigType[]) => configTypes.length === 1 && configTypes[0].type === CONFIG_TYPE_DEFAULT;

export const isFlatFactorGroupLayout = (factorGroups: ConfigType['factorGroups']) =>
  factorGroups.length === 1 && factorGroups[0].name === CONFIG_TYPE_DEFAULT;

export const getFactorLocations = (configTypes: ConfigType[]) =>
  configTypes.flatMap((ct, ctIdx) => ct.factorGroups.flatMap((fg, fgIdx) => fg.factors.map((_, fIdx) => ({ ctIdx, fgIdx, fIdx }))));

export type RiskScoreViewModel = {
  entityWeights: EntityWeight[];
  buildingBlocks: EntityBuildingBlock[];
};

export const transformRiskScoringConfig = ({
  data,
  riskScoreProjectionName,
  getDisplayName,
}: {
  data: GetRiskScoringConfigResponse;
  riskScoreProjectionName: string;
  getDisplayName: (projectionName: string) => string;
}): RiskScoreViewModel => {
  const { projectionToRiskScores, projectionToBaseScores, riskScoreProjectionToBaseScoresMap } = data;

  const baseScoreByProjection = Object.fromEntries(
    projectionToBaseScores.map(({ projectionName, baseScore }) => [projectionName, baseScore])
  );

  const riskScoreEntry = projectionToRiskScores.find(e => e.projectionName === riskScoreProjectionName);
  const weightContributionByProjection = Object.fromEntries(
    (riskScoreEntry?.riskScore.config.baseScoreWeightContribution ?? []).map(entry => [entry.projectionName, entry])
  );

  const mapEntry = riskScoreProjectionToBaseScoresMap.find(e => e.riskScoreProjectionName === riskScoreProjectionName);
  const allowedBaseScores = mapEntry?.baseScores ?? [];

  const entityWeights: EntityWeight[] = allowedBaseScores.map(({ projectionName, allowedAggTypes }) => {
    const contribution = weightContributionByProjection[projectionName];
    const baseScore = baseScoreByProjection[projectionName];
    const factorCount = countFactors(baseScore?.config.configTypes ?? []);
    const hasAggregation = allowedAggTypes.length > 0;

    return {
      projectionId: projectionName,
      name: getDisplayName(projectionName),
      weight: contribution ? Math.round(contribution.weight * 100) : 0,
      factorCount,
      hasAggregation,
      allowedAggTypes,
      aggregation: contribution?.aggType ?? (hasAggregation ? allowedAggTypes[0] : undefined),
    };
  });

  const buildingBlocks: EntityBuildingBlock[] = projectionToBaseScores.map(({ projectionName, baseScore }) => {
    const factors = extractFactors(baseScore.config.configTypes ?? []);

    return {
      projectionId: projectionName,
      name: getDisplayName(projectionName),
      activeCount: factors.length,
      activeLabel: 'Factors',
      factors,
      configTypes: baseScore.config.configTypes ?? [],
    };
  });

  const byOrder = (a: { projectionId: string }, b: { projectionId: string }) =>
    (projToBaseScoreUiConfig[a.projectionId]?.order ?? Infinity) - (projToBaseScoreUiConfig[b.projectionId]?.order ?? Infinity);

  return { entityWeights: entityWeights.sort(byOrder), buildingBlocks: buildingBlocks.sort(byOrder) };
};

const extractFactors = (
  configTypes: GetRiskScoringConfigResponse['projectionToBaseScores'][0]['baseScore']['config']['configTypes']
): KeyFactor[] =>
  Object.values(
    configTypes
      .flatMap(ct => ct.factorGroups)
      .flatMap(fg => fg.factors)
      .reduce(
        (acc, factor) => {
          if (!acc[factor.fieldName]) {
            acc[factor.fieldName] = { name: factor.fieldName, displayName: factor.displayName };
          }
          return acc;
        },
        {} as Record<string, KeyFactor>
      )
  );

const countFactors = (
  configTypes: GetRiskScoringConfigResponse['projectionToBaseScores'][0]['baseScore']['config']['configTypes']
): number => configTypes.reduce((total, ct) => total + ct.factorGroups.reduce((g, fg) => g + fg.factors.length, 0), 0);

export type ProjectionUiConfig = {
  order: number;
  title?: string;
  getIconComponent: (size?: number) => React.ReactElement;
  factorGroupDescriptions?: Record<string, string>;
  fieldGroups?: string[];
  expandable?: boolean;
  activeLabel?: string;
  actionButton?: {
    label: string;
    icon: React.ReactNode;
    path: (typeof PAGE_PATHS)[keyof typeof PAGE_PATHS];
    app?: (typeof APP_PATHS)[keyof typeof APP_PATHS];
  };
};

export const projToBaseScoreUiConfig: Record<string, ProjectionUiConfig> = {
  uber_assets: {
    order: 1,
    title: 'Asset Business Context',
    fieldGroups: ['Asset'],
    getIconComponent: (size = 26) => (
      <AppIconWithBackground
        size={size}
        bgColor="var(--dataviz-color-sequential-blue-scale04)"
        iconColor="var(--color-surface-base-primary)"
        borderRadius={8}>
        <Assets />
      </AppIconWithBackground>
    ),
  },
  uber_findings: {
    order: 2,
    fieldGroups: ['Finding', 'Vulnerability'],
    factorGroupDescriptions: {
      Exposure: 'The potential impact if this vulnerability is exploited.',
      Exploitability: 'How likely and how feasible it is for an attacker to successfully exploit vulnerability.',
    },
    getIconComponent: (size = 26) => (
      <AppIconWithBackground
        size={size}
        bgColor="var(--dataviz-color-severity-primary-low)"
        iconColor="var(--color-surface-base-primary)"
        borderRadius={8}>
        <Findings />
      </AppIconWithBackground>
    ),
  },
  uber_policy_violations: {
    order: 3,
    title: 'Policy Violation Severity',
    expandable: false,
    activeLabel: 'Active Policies',
    actionButton: {
      label: 'See Policies',
      icon: <External style={iconSize(14)} />,
      path: PAGE_PATHS.POLICIES,
      app: APP_PATHS.ASSETS,
    },
    getIconComponent: (size = 26) => (
      <AppIconWithBackground
        size={size}
        bgColor="var(--dataviz-color-categorical-primary-chart20)"
        iconColor="var(--color-surface-base-primary)"
        borderRadius={8}>
        <PolicyViolations />
      </AppIconWithBackground>
    ),
  },
};

const findingTypeToTabDisplayName = {
  CVE: 'CVE',
};

export const getFindingTypeDisplayName = name => findingTypeToTabDisplayName[name] || capitalize(name.toLowerCase());

export type FieldMap = Record<string, { displayName: string }>;

export const getFactorDisplayName = (
  factor: { displayName?: KeyFactor['displayName']; fieldName?: string; name?: string },
  fieldMap: FieldMap
): string => {
  const fieldKey = factor.fieldName || factor.name;
  return factor.displayName ?? (fieldKey ? fieldMap[fieldKey]?.displayName : undefined) ?? fieldKey ?? '';
};

export interface FactorAdapter<TRaw> {
  adapt: (raw: TRaw, fieldMap: FieldMap) => EditFactorType;
  serialize: (factor: EditFactorType) => TRaw;
}

export const riskScoreFactorAdapter: FactorAdapter<RiskFactor> = {
  adapt: (raw, fieldMap) => ({
    name: raw.displayName ?? fieldMap[raw.fieldName]?.displayName ?? raw.fieldName,
    fieldName: raw.fieldName,
    fallBackScore: raw.fallback,
    scoringFilters: raw.scoringRules.map(({ value, filter, adjustmentType }) => ({ score: value, filter, adjustmentType })),
    minPoints: 0,
    maxPoints: 0,
  }),
  serialize: factor => ({
    displayName: factor.name,
    fieldName: factor.fieldName!,
    fallback: factor.fallBackScore,
    scoringRules: factor.scoringFilters.map(({ score, filter, adjustmentType }) => ({
      value: score,
      filter,
      adjustmentType,
    })) as unknown as ScoringRule[],
  }),
};

export const toEntitiesFactor = (projectionId: string, factors: EditFactorType[]): EntitiesFactor => ({
  projectionId: { name: projectionId, builtIn: false },
  minPoints: 0,
  maxPoints: 0,
  factors,
});

const untitledRegex = /^untitled\s\d+$/i;

const getEmptyFactorName = (entitiesFactor: EntitiesFactor): string => {
  const factorNames = entitiesFactor.factors.map(f => f.name);
  const lastUntitled = factorNames.findLast(name => untitledRegex.test(name.toLowerCase()));
  if (!lastUntitled) return 'Untitled 1';
  const [header, index] = lastUntitled.split(' ');
  return `${header} ${Number(index) + 1}`;
};

export const emptyEditFactor = (entitiesFactor: EntitiesFactor): EditFactorType => ({
  added: true,
  fallBackScore: 0,
  fieldName: undefined,
  maxPoints: 0,
  minPoints: 0,
  name: getEmptyFactorName(entitiesFactor),
  scoringFilters: [],
});

export { getScoreCondition, emptyFilter } from '../../components/scoring/utils';

export const WEIGHT_ERROR_MESSAGE = 'Total contribution must equal 100%';
export const hasWeightError = (entityWeights: EntityWeight[]): boolean => entityWeights.reduce((sum, e) => sum + e.weight, 0) !== 100;

export const hasFactorGroupWeightError = (configTypes: ConfigType[], activeConfigTypeIndex: number): boolean => {
  const activeConfigType = configTypes[activeConfigTypeIndex];
  if (!activeConfigType) return false;
  const total = activeConfigType.factorGroups.reduce((sum, fg) => sum + Math.round(fg.weight * 100), 0);
  return total !== 100;
};

export const numberInputProps = {
  actionIcons: [<Box sx={{ color: 'var(--color-content-base-primary)' }}>%</Box>],
  size: 'small',
  disableClearButton: true,
  type: 'number',
  style: { width: 100 },
};

export type RiskScoreChanges = {
  changedBaseScoreNames: string[];
  impactedCompoundNames: string[];
};

export const getRiskScoreChanges = ({
  currentBaseScores,
  initialBaseScores,
  data,
  getDisplayName,
}: {
  currentBaseScores: { projectionName: string; baseScore: BaseScore }[];
  initialBaseScores: { projectionName: string; baseScore: BaseScore }[];
  data: GetRiskScoringConfigResponse;
  getDisplayName: (projectionName: string) => string;
}): RiskScoreChanges => {
  const initialByProjection = Object.fromEntries(initialBaseScores.map(bs => [bs.projectionName, bs]));

  const changedBaseScores = currentBaseScores.filter(bs => !isDeepEqual(bs, initialByProjection[bs.projectionName]));

  const changedIds = new Set(changedBaseScores.map(bs => bs.projectionName));

  const changedBaseScoreNames = changedBaseScores.map(bs => getDisplayName(bs.projectionName));

  const impactedCompoundNames = data.riskScoreProjectionToBaseScoresMap
    .filter(entry => entry.baseScores.some(bs => changedIds.has(bs.projectionName)))
    .map(entry => getDisplayName(entry.riskScoreProjectionName));

  return {
    changedBaseScoreNames,
    impactedCompoundNames,
  };
};
